import React, { useState, useEffect, useContext } from "react";

// Languages
import { useTranslation } from "react-i18next";
import { CiHeart } from "react-icons/ci";
import { FaHeart } from "react-icons/fa";
// CSS
import "./hotelobject.css";

// Components
import StarRating from "../../../StarRating/starrating";
import Pricing from "../Pricing/pricing";
// Import our carousel
import HotelCarousel from "../../../Agent/SearchAgent/SearchResults/hotelcarousel";

// Icons
import WhatsAppIcon from "../../../../assets/images/icons/whatsapp.png";
import EmailIcon from "../../../../assets/images/icons/email.png";
import NoBeds from "../../../../assets/images/icons/bed.png";

// Contexts
import { SubTitle, Title } from "../../../Text";
import { useSearch } from "../../../../contexts/SearchContext";

// Create a context to share all hotels between components
const HotelsContext = React.createContext({
  allHotels: [],
  currentIndex: 0
});

// Create a provider component that will be used in SearchResults
export const HotelsProvider = ({ children, hotels = [] }) => {
  return (
    <HotelsContext.Provider value={{ allHotels: hotels }}>
      {children}
    </HotelsContext.Provider>
  );
};

const HotelObject = ({ hotelsItem, searchSession, isHighlighted = false  , likeHotel , savedHotels}) => {
  // Translator
  const { t } = useTranslation();
  const { getSearchPhrase } = useSearch();
  
  // Access the hotels context
  const { allHotels } = useContext(HotelsContext);
  
  // States
  const [totalRooms, setTotalRooms] = useState("");
  const [totalAdults, setTotalAdults] = useState("");
  const [totalKids, setTotalKids] = useState("");
  const [numberOfNights, setNumberOfNights] = useState("");
  const [showCarousel, setShowCarousel] = useState(false);
  
  const currentClient = localStorage.getItem("agent-saved-client-info")
    ? JSON.parse(localStorage.getItem("agent-saved-client-info"))
    : null;



  useEffect(() => {
    extractDataFromSearch();
  }, []);


  // Scroll into view if hotel is highlighted from map
  useEffect(() => {
    if (isHighlighted) {
      const element = document.getElementById(hotelsItem.ID);
      if (element) {
        element.scrollIntoView({ behavior: "smooth", block: "center" });
      }
    }
  }, [isHighlighted, hotelsItem.ID]);

  // Find index of current hotel in the allHotels array
  const findInitialIndex = () => {
    if (!allHotels || allHotels.length === 0) return 0;
    
    const index = allHotels.findIndex(hotel => hotel.ID === hotelsItem.ID);
    return index >= 0 ? index : 0;
  };


  // Handle click on hotel image - open carousel
  const handleImageClick = () => {
    setShowCarousel(true);
  };

  const isHotelLiked = (id) => {
    // No need to parse here since savedHotels is already an array
    return savedHotels.includes(id);
  };


  const extractDataFromSearch = async () => {
    const searchPhrase = await getSearchPhrase;
    const { rooms } = searchPhrase || {
      rooms: [],
    };

    if (rooms && rooms.length > 0) {
      let totalAdults = 0;
      let totalKids = 0;
      let totalRooms = rooms.length;

      rooms.forEach((room) => {
        totalAdults += room.AdultsCount;
        if (room.KidsAges && room.KidsAges.length > 0) {
          totalKids += room.KidsAges.length;
        }
      });

      // Get number of nights
      const checkInParts = searchPhrase.checkIn.split("/");
      const checkOutParts = searchPhrase.checkOut.split("/");

      // Create Date objects using parts in the format: year, month - 1, day
      const checkInDate = new Date(
        checkInParts[2],
        checkInParts[1] - 1,
        checkInParts[0]
      );
      const checkOutDate = new Date(
        checkOutParts[2],
        checkOutParts[1] - 1,
        checkOutParts[0]
      );

      // Calculate the time difference in milliseconds
      const timeDifference = checkOutDate - checkInDate;

      // Convert milliseconds to days
      const numberOfNights = Math.floor(timeDifference / (1000 * 60 * 60 * 24));

      setTotalRooms(totalRooms);
      setTotalAdults(totalAdults);
      setTotalKids(totalKids);
      setNumberOfNights(numberOfNights);
    }
  };

  return (
    <>
      <div
        id={hotelsItem.ID}
        className={`hotelObject-wrapper h-full ${isHighlighted ? "highlighted" : ""}  tw-grid-cols-1 tw-z-0`}
      >
        {/* Hotel Info */}
        <div className="hotelObject-info-wrapper">
          {/* Hotel Images - now clickable to open carousel */}
          {hotelsItem?.DefaultImage?.FullSize ? (
            <div className="hotelObject-display-image-wrapper">
              <button className="likehotel" onClick={() => likeHotel(hotelsItem.ID.toString())}>{isHotelLiked(hotelsItem.ID.toString()) ? <FaHeart /> : <CiHeart />}</button>
              <img
                alt={hotelsItem.DisplayName}
                onClick={handleImageClick}
                className="hotelObject-display-image cursor-pointer" /* Add cursor pointer to indicate it's clickable */
                src={hotelsItem.DefaultImage.FullSize}
              />
            </div>
          ) : (
            <div className="hotelObject-nobedimage-wrapper-wrapper">
              <div className="hotelObject-nobedimage-wrapper">
                <img
                  className="roomPlacer-nobedimage-img"
                  src={NoBeds}
                  alt="No hotel images"
                />
                <label className="roomPlacer-nobedimage-title tw-text-sm">
                  {t("hotelprofile.nohotelimages")}
                </label>
              </div>
            </div>
          )}

          {/* Hotel Description */}
          <div
            className={`hotelObject-description-wrapper ${
              document.documentElement.dir === "ltr"
                ? "direction-rtl"
                : "direction-ltr"
            } tw-py-2`}
          >
            {/* Description */}
            <div className="hotelObject-description">
              <div className="hotelObject-title-wrapper tw-mb-1">
                {/* Star Rating */}
                <StarRating rating={hotelsItem.StarRating} />

                <Title className="hotelObject-title tw-text-sm lg:tw-text-lg tw-line-clamp-1 tw-m-0">
                  {hotelsItem.DisplayName}
                </Title>
                <SubTitle className="hotelObject-sub-title tw-text-sm tw-line-clamp-1 tw-mt-0 tw-mb-0">
                  {hotelsItem?.Address}
                </SubTitle>
                {hotelsItem?.distance && (
                  <SubTitle className="hotelObject-distance tw-text-sm tw-m-0">
                    {hotelsItem.distance.toFixed(2)} ק"מ מהיעד
                  </SubTitle>
                )}
              </div>

              <div
                className={`${
                  document.documentElement.dir === "rtl"
                    ? "direction-rtl"
                    : "direction-ltr"
                } tw-text-xs`}
              >
                <span className="hotelObject-text">
                  {numberOfNights} {t("hotelsearchbar.nights")} {totalRooms}{" "}
                  {t("hotelsearchbar.rooms")} {totalAdults}{" "}
                  {t("hotelsearchbar.adults")}{" "}
                  {totalKids > 0
                    ? `${totalKids} ${t("hotelsearchbar.kids")}`
                    : ""}
                </span>
              </div>
            </div>

            {/* Share */}
            <div className="hotelObject-share">
              {/* Share options */}
              <div className="hotelObject-icon-wrapper">
                {currentClient?.phone && (
                  <a
                    href={`https://wa.me/${currentClient.phone}`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <img
                      className="hotelObject-icon"
                      src={WhatsAppIcon}
                      alt="Whatsapp Icon"
                    />
                  </a>
                )}

                {currentClient?.email && (
                  <a href={`mailto:${currentClient.email}`}>
                    <img
                      className="hotelObject-icon"
                      src={EmailIcon}
                      alt="Email Icon"
                    />
                  </a>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Pricing */}
        <div className="hotelObject-pricing tw-flex tw-justify-center tw-items-center">
          <Pricing item={hotelsItem} searchSession={searchSession} />
        </div>
      </div>

      {/* Hotel Carousel */}
      {showCarousel && (
        <HotelCarousel
          hotels={allHotels.length > 0 ? allHotels : [hotelsItem]}
          visible={showCarousel}
          onClose={() => setShowCarousel(false)}
          initialIndex={findInitialIndex()}
          totalKids={totalKids}
          totalAdults={totalAdults}
          totalRooms={totalRooms}
          numberOfNights={numberOfNights}
          currentClient={currentClient}
          searchSession={searchSession}
        />
      )}
    </>
  );
};

export default HotelObject;